/* eslint-disable no-bitwise */
/* eslint-disable import/no-extraneous-dependencies */
import { useEffect, useState } from "react";
import { HexColorPicker, RgbaStringColorPicker } from "react-colorful";
import {
  RiArrowDownSLine,
  RiArrowGoBackLine,
  RiArrowUpSLine,
} from "react-icons/ri";
import { MdOutlineModeEditOutline } from "react-icons/md";
import { IoClose } from "react-icons/io5";
import { postCriarEtiqueta } from "services/api/AppWeb_Lead/criar-etiqueta";
import { toast } from "react-toastify";
import { CircularProgress } from "@mui/material";
import DialogCommon from "components/dialogCommon";
import { LiaTagSolid } from "react-icons/lia";
import * as Styles from "./styles";

export const TagEditor = ({
  isOpen,
  onClose,
  tags,
  refetch,
  openEditing,
}: {
  isOpen: boolean;
  onClose: () => void;
  tags: {
    coresSalvas: string[];
    etiquetasCorretor: { id: string; nome: string; cor: string }[];
  };
  refetch: Function;
  openEditing?: string;
}) => {
  const [name, setName] = useState("");
  const [color, setColor] = useState("#884444");
  const [colorFormat, setColorFormat] = useState<"Hex" | "Rgba">("Hex");
  const [tags_, setTags_] = useState(tags);
  const [confirm, setConfirm] = useState(false);

  const [r, setR] = useState<{
    saving: boolean;
    editing: string | null;
    deleting: string[];
  }>({
    saving: false,
    editing: null,
    deleting: [],
  });

  useEffect(() => {
    if (isOpen) {
      if (openEditing) {
        const et_ = tags?.etiquetasCorretor?.find(
          (et) => et.id === openEditing
        );
        setName(et_?.nome as string);
        setColor(et_?.cor as string);
        setR((prev) => ({ ...prev, editing: openEditing }));
      } else reset();
    } else reset();
  }, [isOpen]);

  const toggleFormat = () => {
    setColorFormat((prev) => (prev === "Hex" ? "Rgba" : "Hex"));
    setColor(convertColor(color, colorFormat));
  };

  const handleColorSelection = (c: string) => {
    if (colorFormat === "Hex") {
      if (!c.startsWith("#")) {
        const c_ = convertColor(c, "Rgba");
        setColor(c_);
      } else {
        setColor(c);
      }
    } else if (colorFormat === "Rgba") {
      if (c.startsWith("#")) {
        const c_ = convertColor(c, "Hex");
        setColor(c_);
      } else {
        setColor(c);
      }
    }
  };

  const convertColor = (c: string, cF: "Hex" | "Rgba"): string => {
    if (cF === "Hex") {
      c = c.replace(/^#/, "");

      let r;
      let g;
      let b;

      if (c.length === 6) {
        const bigint = parseInt(c, 16);
        r = (bigint >> 16) & 255;
        g = (bigint >> 8) & 255;
        b = bigint & 255;
      } else if (c.length === 3) {
        r = parseInt(c[0] + c[0], 16);
        g = parseInt(c[1] + c[1], 16);
        b = parseInt(c[2] + c[2], 16);
      } else {
        return "rgba(0, 0, 0, 1)";
      }

      return `rgba(${r}, ${g}, ${b}, 1)`;
    }
    if (cF === "Rgba") {
      const rgbaRegex =
        /rgba?\(\s*(\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d*\.?\d+))?\s*\)/i;
      const match = rgbaRegex.exec(c);

      if (!match) {
        return "#884444";
      }

      const r = parseInt(match[1], 10);
      const g = parseInt(match[2], 10);
      const b = parseInt(match[3], 10);
      const a = match[4] ? parseFloat(match[4]) : 1;

      const hexR = r.toString(16).padStart(2, "0");
      const hexG = g.toString(16).padStart(2, "0");
      const hexB = b.toString(16).padStart(2, "0");
      const hexA = Math.round(a * 255)
        .toString(16)
        .padStart(2, "0");

      return a === 1
        ? `#${hexR}${hexG}${hexB}`
        : `#${hexR}${hexG}${hexB}${hexA}`;
    }
    return "";
  };

  const reset = () => {
    setName("");
    setColor("#884444");
    setColorFormat("Hex");
    setConfirm(false);
    setR({ saving: false, editing: null, deleting: [] });
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleAdd = () => {
    const etiquetasCorretor_ = [...(tags_?.etiquetasCorretor || [])];

    let randomId = 0;
    do {
      randomId = Math.floor(Math.random() * 1000000);
      // eslint-disable-next-line no-loop-func
    } while (etiquetasCorretor_.find((et) => et.id === `_${randomId}`));

    etiquetasCorretor_.push({ id: `_${randomId}`, cor: color, nome: name });

    setName("");
    setColor("#884444");
    setTags_({ ...tags_, etiquetasCorretor: etiquetasCorretor_ });
  };

  const handleEdit = ({
    idEtiqueta,
    name,
    edit,
    done,
  }: {
    idEtiqueta?: string;
    name?: string;
    edit: boolean;
    done?: boolean;
  }) => {
    if (done) {
      const etiquetasCorretor_ = [...(tags_?.etiquetasCorretor || [])];

      const index = etiquetasCorretor_.findIndex((et) => et.id === r.editing);

      if (index !== -1) {
        etiquetasCorretor_[index] = {
          ...etiquetasCorretor_[index],
          nome: name ?? "",
          cor: color,
        };
      }

      setTags_({ ...tags_, etiquetasCorretor: etiquetasCorretor_ });
    }

    setR((prev) => ({
      ...prev,
      editing: edit && idEtiqueta ? idEtiqueta : null,
    }));
    setName(edit && name ? name : "");
    setColor(
      edit
        ? (tags_?.etiquetasCorretor?.find((et) => et.id === idEtiqueta)
            ?.cor as string)
        : "#884444"
    );
  };

  const handleSave = () => {
    setConfirm(false);
    setR((prev) => ({ ...prev, saving: true }));

    const etiquetasCorretor = (tags_?.etiquetasCorretor || [])
      .filter((etiqueta) => !r.deleting.includes(etiqueta.id))
      .map((etiqueta) => ({
        ...etiqueta,
        id: etiqueta.id.startsWith("_") ? "" : etiqueta.id,
      }));

    postCriarEtiqueta({ etiquetasCorretor })
      .then(({ success, data }) => {
        if (success) {
          toast.success(data);
          handleClose();
          refetch();
        } else {
          throw new Error();
        }
      })
      .catch((err) => {
        console.log(err);
        toast.error(
          "Erro ao criar etiqueta, por favor tente novamente mais tarde."
        );
      })
      .finally(() => {
        setR((prev) => ({ ...prev, saving: false }));
      });
  };

  return (
    <Styles.Container>
      <Styles.Subtitle>
        {
          "Crie etiquetas personalizadas para organizar e identificar seus leads com mais facilidade"
        }
      </Styles.Subtitle>

      <Styles.NameContainer>
        <p>Nome da Etiqueta</p>
        <div>
          <input
            placeholder="Exemplo: Urgente"
            value={name}
            onChange={(e: any) => setName(e.target.value)}
          />
        </div>
      </Styles.NameContainer>

      <Styles.ColorPickerContainer>
        {colorFormat === "Hex" ? (
          <HexColorPicker
            color={color}
            onChange={setColor}
            style={{
              width: "100%",
            }}
          />
        ) : (
          <RgbaStringColorPicker
            color={color}
            onChange={setColor}
            style={{
              width: "100%",
            }}
          />
        )}
      </Styles.ColorPickerContainer>

      <Styles.ColorPickerDetails>
        <Styles.HexOrRgba>
          <p>{colorFormat}</p>
          <div className="icons">
            <RiArrowUpSLine
              onClick={toggleFormat}
              style={{ cursor: "pointer" }}
              size={12}
            />
            <RiArrowDownSLine
              onClick={toggleFormat}
              style={{ cursor: "pointer" }}
              size={12}
            />
          </div>
        </Styles.HexOrRgba>

        {colorFormat === "Hex" ? (
          <Styles.HexInput>
            <p>{"#"}</p>
            <input
              maxLength={6}
              value={color.replace("#", "")}
              onChange={(e) => setColor(`#${e.target.value}`)}
            />
          </Styles.HexInput>
        ) : (
          <Styles.HexInput>
            <input value={color} onChange={(e) => setColor(e.target.value)} />
          </Styles.HexInput>
        )}
      </Styles.ColorPickerDetails>

      <Styles.ColorsHeader>
        <p>Cores salvas</p>
      </Styles.ColorsHeader>

      <Styles.Colors>
        {tags_?.coresSalvas &&
          tags_?.coresSalvas.slice(0, 7).map((color_: string) => (
            <div
              key={color_}
              style={{ display: "flex", justifyContent: "center" }}
            >
              <Styles.Color
                color={color_}
                selected={color_ === color}
                onClick={() => handleColorSelection(color_)}
              />
            </div>
          ))}
        {tags_?.coresSalvas?.length > 7 &&
          tags_.coresSalvas.slice(7).map((color_: string) => (
            <div
              key={color_}
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: 6,
              }}
            >
              <Styles.Color
                color={color_}
                selected={color_ === color}
                onClick={() => handleColorSelection(color_)}
              />
            </div>
          ))}
      </Styles.Colors>

      <Styles.TagsWrapper>
        <Styles.ColorsHeader>
          <h1>Minhas etiquetas</h1>
          {r.editing ? (
            <div style={{ display: "flex", gap: 4 }}>
              <Styles.AddColor
                style={{ backgroundColor: "transparent" }}
                onClick={() => handleEdit({ edit: false })}
              >
                <p style={{ color: "red" }}>Cancelar</p>
              </Styles.AddColor>
              <Styles.AddColor
                disabled={name.trim() === ""}
                onClick={() =>
                  name.trim() !== "" &&
                  handleEdit({ name, edit: false, done: true })
                }
              >
                <p>Salvar</p>
              </Styles.AddColor>
            </div>
          ) : (
            <Styles.AddColor
              disabled={name.trim() === ""}
              onClick={() => name.trim() !== "" && handleAdd()}
            >
              <p>+ Adicionar</p>
            </Styles.AddColor>
          )}
        </Styles.ColorsHeader>
        <Styles.TagsContainer>
          {tags_?.etiquetasCorretor?.length > 0 &&
            tags_?.etiquetasCorretor?.map(
              (tag: { id: string; nome: string; cor: string }) => (
                <Styles.Tag
                  key={tag.id}
                  color={r.deleting.includes(tag?.id) ? "#B3B3B3CC" : tag.cor}
                  selected={tag.id === r.editing}
                >
                  <p
                    style={
                      r.deleting.includes(tag?.id)
                        ? { textDecoration: "line-through" }
                        : {}
                    }
                  >
                    {tag.nome}
                    {r.deleting.includes(tag.id) ? (
                      <RiArrowGoBackLine
                        size={14}
                        className="icons"
                        onClick={() => {
                          setR((prev) => ({
                            ...prev,
                            deleting: prev.deleting.filter(
                              (id) => id !== tag.id
                            ),
                          }));
                        }}
                      />
                    ) : (
                      <>
                        <MdOutlineModeEditOutline
                          size={14}
                          className="icons"
                          onClick={() =>
                            !r.editing &&
                            handleEdit({
                              idEtiqueta: tag.id,
                              name: tag.nome,
                              edit: true,
                            })
                          }
                        />
                        <IoClose
                          size={16}
                          className="icons"
                          onClick={() =>
                            setR((prev) => ({
                              ...prev,
                              deleting: [...prev.deleting, tag.id],
                            }))
                          }
                        />
                      </>
                    )}
                  </p>
                </Styles.Tag>
              )
            )}
        </Styles.TagsContainer>
      </Styles.TagsWrapper>

      <Styles.Buttons>
        <button className="secondary" type="button" onClick={handleClose}>
          Fechar
        </button>
        <button
          className={`primary ${r.saving ? "disabled" : ""}`}
          type="button"
          disabled={r.saving}
          onClick={() => setConfirm(true)}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {r.saving ? (
              <CircularProgress
                size={20}
                style={{ color: "#fff", padding: 0, margin: 0 }}
              />
            ) : (
              "Salvar"
            )}
          </div>
        </button>
      </Styles.Buttons>

      <DialogCommon
        closeText="Cancelar"
        saveText="Sim"
        onClose={() => setConfirm(false)}
        onSave={() => {}}
        isRight={false}
        titleHeader="Tem certeza que deseja salvar?"
        isFilter={false}
        isClose
        width={400}
        component={
          <Styles.ConfirmContainer>
            <p>
              {
                "Ao salvar as alterações, as etiquetas vinculadas aos Leads serão atualizadas."
              }
            </p>
            <Styles.Buttons>
              <button
                className="secondary"
                type="button"
                onClick={() => setConfirm(false)}
              >
                Cancelar
              </button>
              <button className="primary" type="button" onClick={handleSave}>
                Sim
              </button>
            </Styles.Buttons>
          </Styles.ConfirmContainer>
        }
        setOpenModal={setConfirm}
        open={confirm}
        icon={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#819DFF",
              border: "8px solid #D5DEFF",
              padding: 2,
              borderRadius: "50%",
              height: 52,
              width: 52,
              marginRight: 8,
            }}
          >
            <LiaTagSolid color="white" size={24} style={{ margin: 0 }} />
          </div>
        }
      />
    </Styles.Container>
  );
};
